const DOCUMENT_ID_UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** Custom file URL scheme used when reloading a general-chat attachment chip. */
export const DOCUMENT_FILE_URL_PREFIX = "doculegis-document:";

const MARKER_WITH_ID =
  /^Attached file:\s*(.+?)\s*\(documentId:\s*([0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})\)\s*$/i;

const MARKER_LEGACY = /^Attached file:\s*(.+?)\s*$/i;

export type ChatAttachmentRef = {
  filename: string;
  documentId?: string;
};

export function formatAttachedFileMarker(filename: string, documentId: string): string {
  const name = filename.trim() || "file";
  return `Attached file: ${name} (documentId: ${documentId})`;
}

export function buildUserTextWithAttachmentMarker(
  text: string,
  filename: string,
  documentId: string,
): string {
  const marker = formatAttachedFileMarker(filename, documentId);
  const trimmed = text.trim();
  return trimmed ? `${trimmed}\n${marker}` : marker;
}

export function parseAttachedFileMarker(line: string): ChatAttachmentRef | null {
  const trimmed = line.trim();
  if (!trimmed) return null;
  const withId = MARKER_WITH_ID.exec(trimmed);
  if (withId) {
    const filename = withId[1]?.trim() ?? "";
    const documentId = withId[2]?.trim() ?? "";
    if (!filename || !DOCUMENT_ID_UUID.test(documentId)) return null;
    return { filename, documentId };
  }
  const legacy = MARKER_LEGACY.exec(trimmed);
  if (!legacy) return null;
  const filename = legacy[1]?.trim() ?? "";
  if (!filename) return null;
  // Reject lines that look like the with-id form but failed UUID validation.
  if (/\(documentId:\s*[^)]+\)\s*$/i.test(trimmed)) return null;
  return { filename };
}

export function documentIdFromFileUrl(url: string | undefined): string | undefined {
  if (typeof url !== "string" || !url.startsWith(DOCUMENT_FILE_URL_PREFIX)) return undefined;
  const id = url.slice(DOCUMENT_FILE_URL_PREFIX.length).trim();
  return DOCUMENT_ID_UUID.test(id) ? id : undefined;
}

export function fileUrlForDocumentId(documentId: string): string {
  return `${DOCUMENT_FILE_URL_PREFIX}${documentId}`;
}

/**
 * Split user text parts into visible body text and attachment refs from
 * `Attached file:` marker lines (with or without documentId).
 */
export function splitUserTextForAttachments(texts: string[]): {
  attachments: ChatAttachmentRef[];
  visibleTexts: string[];
} {
  const attachments: ChatAttachmentRef[] = [];
  const visibleTexts: string[] = [];

  for (const block of texts) {
    const lines = block.split("\n");
    const kept: string[] = [];
    for (const line of lines) {
      const marker = parseAttachedFileMarker(line);
      if (marker) {
        attachments.push(marker);
        continue;
      }
      kept.push(line);
    }
    const next = kept.join("\n").trim();
    if (next) visibleTexts.push(next);
  }

  return { attachments, visibleTexts };
}
